import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';

const Products = () => {
  const [params, setParams] = useSearchParams();
  const concern = params.get('concern');
  const [products, setProducts] = useState([]);
  const [concerns, setConcerns] = useState([]);

  useEffect(() => {
    const url = concern ? `/shop/products?concern=${concern}` : '/shop/products';
    api.get(url).then(({ data }) => setProducts(data));
    api.get('/content/health-concerns').then(({ data }) => setConcerns(data));
  }, [concern]);

  const concernByslug = useMemo(() => Object.fromEntries(concerns.map((c) => [c.slug, c])), [concerns]);
  const activeConcern = concernByslug[concern];

  return (
    <div className="bg-sans_bone">
      <section className="sans-section">
        <div className="sans-container">
          <div className="sans-label mb-6" style={activeConcern ? { color: activeConcern.color } : undefined}>
            {activeConcern ? `For ${activeConcern.name}` : 'Products'}
          </div>
          <h1 className="sans-h1">Classical formulations,<br/>quietly made.</h1>
          <p className="sans-body mt-8 max-w-xl">
            Every product is rooted in classical Ayurvedic texts, made with authenticated herbs.
          </p>

          <div className="mt-14 flex flex-wrap gap-2">
            <button
              onClick={() => setParams({})}
              className={`px-4 py-2 text-xs uppercase tracking-[0.18em] border transition-all ${!concern ? 'bg-sans_ink text-sans_bone border-sans_ink' : 'bg-white border-sans_line text-sans_ink hover:border-sans_ink'}`}
              data-testid="filter-all"
            >All</button>
            {concerns.map((c) => {
              const active = concern === c.slug;
              return (
                <button
                  key={c.slug}
                  onClick={() => setParams({ concern: c.slug })}
                  className={`px-4 py-2 text-xs uppercase tracking-[0.18em] border transition-all inline-flex items-center gap-2 ${active ? 'text-white' : 'bg-white text-sans_ink hover:border-sans_ink'}`}
                  style={active
                    ? { background: c.color, borderColor: c.color }
                    : { borderColor: '#E5E3DB' }}
                  data-testid={`filter-${c.slug}`}
                >
                  <span aria-hidden className="w-1.5 h-1.5 rounded-full" style={{ background: active ? '#fff' : c.color }} />
                  {c.name}
                </button>
              );
            })}
          </div>

          <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-10">
            {products.map((p) => {
              const primaryConcernSlug = (p.tags || [])[0];
              const primaryConcern = concernByslug[primaryConcernSlug];
              const accent = p.accent || primaryConcern?.color;
              return (
                <Link key={p.product_id} to={`/products/${p.slug}`} data-testid={`product-card-${p.slug}`} className="group block">
                  <div className="aspect-square bg-sans_bone overflow-hidden relative flex items-center justify-center p-4 md:p-5 border border-sans_line">
                    <img src={p.image} alt={p.name} className="max-w-full max-h-full object-contain group-hover:scale-[1.05] transition-transform duration-700" loading="lazy" />
                    {accent && (
                      <span
                        aria-hidden
                        className="absolute top-0 left-0 w-1 h-full transition-all group-hover:w-1.5"
                        style={{ background: accent }}
                      />
                    )}
                  </div>
                  <div className="mt-5">
                    {primaryConcern && (
                      <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] mb-1.5" style={{ color: accent }}>
                        <span aria-hidden className="w-1 h-1 rounded-full" style={{ background: accent }} />
                        {primaryConcern.name}
                      </div>
                    )}
                    <div className="text-[10px] uppercase tracking-[0.2em] text-sans_mute">{p.size}</div>
                    <div className="font-serif text-xl text-sans_ink mt-1">{p.name}</div>
                    <div className="text-sm text-sans_mute mt-1">{p.tagline}</div>
                    <div className="mt-3 flex items-baseline gap-3">
                      <span className="text-sm text-sans_ink">₹{p.price}</span>
                      {p.compare_at && p.compare_at > p.price && (
                        <span className="text-xs text-sans_mute line-through">₹{p.compare_at}</span>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
          {products.length === 0 && (
            <div className="mt-20 text-center text-sans_mute">No products match this filter yet.</div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Products;
